// reelCropUtils.js
//
// Renders the user's crop selection (as reported by react-easy-crop) onto an
// off-screen canvas at a FIXED output resolution, so every uploaded moment is
// byte-for-byte the same reel ratio regardless of the source photo's size or
// orientation. 1080x1920 matches Instagram/TikTok reel export resolution.

export const REEL_ASPECT = 9 / 16;
export const REEL_OUTPUT_WIDTH = 1080;
export const REEL_OUTPUT_HEIGHT = 1920;

/**
 * Loads an image and resolves once it's ready to be drawn to canvas.
 */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (err) => reject(err));
    img.setAttribute('crossOrigin', 'anonymous'); // avoid canvas tainting for remote/blob URLs
    img.src = src;
  });
}

/**
 * @param {string} imageSrc - data URL or object URL of the source image
 * @param {{x:number,y:number,width:number,height:number}} croppedAreaPixels - from react-easy-crop's onCropComplete
 * @param {number} outputWidth
 * @param {number} outputHeight
 * @param {'image/jpeg'|'image/webp'|'image/png'} mimeType
 * @param {number} quality - 0..1, ignored for png
 * @returns {Promise<{blob: Blob, dataUrl: string}>}
 */
export async function getCroppedReelImage(
  imageSrc,
  croppedAreaPixels,
  outputWidth = REEL_OUTPUT_WIDTH,
  outputHeight = REEL_OUTPUT_HEIGHT,
  mimeType = 'image/jpeg',
  quality = 0.92
) {
  const image = await loadImage(imageSrc);

  const canvas = document.createElement('canvas');
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    outputWidth,
    outputHeight
  );

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, mimeType, quality));
  const dataUrl = canvas.toDataURL(mimeType, quality);

  return { blob, dataUrl };
}

/**
 * Wraps a Blob as a File so it can be appended straight into FormData for upload.
 */
export function blobToFile(blob, filename = 'moment.jpg') {
  return new File([blob], filename, { type: blob.type });
}

/**
 * Rejects images that are too small to fill the reel frame at a reasonable
 * quality (avoids letting someone upload a tiny thumbnail that would come out
 * blurry once stretched to 1080x1920).
 */
export function isImageLargeEnough(naturalWidth, naturalHeight, minWidth = 480, minHeight = 854) {
  return naturalWidth >= minWidth && naturalHeight >= minHeight;
}